import React, { useContext, useState } from 'react';
import { Skeleton } from 'antd';
import { Form, Field } from 'react-final-form';
import createDecorator from 'final-form-focus';
import styled from 'styled-components';

import {
  AUTOCOMPLETE_TYPES,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  SVG_ICON_SIZES,
  SVG_ICON_COLORS,
} from '@twicapp/ui/constants/properties';
import { EMPLOYEE_PROFILE_ELEMENTS } from 'constants/testSelectors';

import Avatar from 'components/common/Avatar';
import Headline1 from '@twicapp/ui/components/Headline1';
import Select from '@twicapp/ui/components/Select';
import Option from '@twicapp/ui/components/Option';
import Title1 from '@twicapp/ui/components/Title1';
import Body2 from '@twicapp/ui/components/Body2';
import TextField from '@twicapp/ui/components/TextField';
import SvgIcon from '@twicapp/ui/components/SvgIcon';
import Button from '@twicapp/ui/components/Button';
import { SCREEN_WIDTHS } from '@twicapp/ui/constants/breakpoints';
import useSnackbar from '@twicapp/ui/hooks/useSnackbar';

import { ReactComponent as LockSvg } from '@twicapp/ui/icons/lock.svg';
import { ReactComponent as HelpSvg } from '@twicapp/ui/icons/help.svg';

import * as SettingApis from 'apis/setting';

import * as EmployeeUtils from 'utils/employee';

import ProfileContext from 'contexts/ProfileContext';
import PretaxContext from 'contexts/PretaxContext';

import { US_STATES } from 'constants/locations';

import Head from 'components/common/Head';

const Container = styled.div``;
const FieldsContainer = styled.div``;

const PageHeader = styled(Headline1)`
  margin: 0px 24px 24px 0px;
`;

const SectionHeader = styled(Title1)`
  margin-bottom: 32px;
`;

const FormContainer = styled.form``;

const InfoInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: 32px;
  margin-bottom: 20px;
`;

const StyledLockIcon = styled(SvgIcon).attrs({
  icon: LockSvg,
  stroke: SVG_ICON_COLORS.SECONDARY,
  fill: SVG_ICON_COLORS.LIGHT,
  size: SVG_ICON_SIZES.MEDIUM,
})``;

const StyledHelpIcon = styled(SvgIcon).attrs({
  icon: HelpSvg,
  stroke: SVG_ICON_COLORS.SECONDARY,
})``;

const FirstNameField = styled(TextField).attrs({
  end_icon: StyledLockIcon,
  disabled: true,
  end_icon_tooltip_text: 'Disabled',
})`
  width: 100%;
`;

const LastNameField = styled(TextField).attrs({
  end_icon: StyledLockIcon,
  disabled: true,
  end_icon_tooltip_text: 'Disabled',
})`
  width: 100%;
`;

const NameRow = styled.div`
  margin-bottom: 32px;
  display: flex;
  gap: 20px;
  flex-direction: column;
  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const PreferredNameRow = styled.div``;

const PreferredNameField = styled(TextField)`
  width: 100%;
`;

const ResidenceInputContainer = styled.div`
  margin-bottom: 42px;
  margin-top: 32px;
`;

const AddressContainer = styled.div`
  margin-top: 22px;
`;

const StreetAddress1Field = styled(TextField)`
  width: 100%;
  margin-bottom: 32px;
`;
const StreetAddress2Field = styled(TextField)`
  width: 100%;
  margin-bottom: 32px;
`;

interface CityFieldProps {
  is_usa: boolean;
}
const CityField = styled(TextField)<CityFieldProps>`
  width: ${({ is_usa }) => (is_usa ? '100%' : '49%')};
  margin-right: ${({ is_usa }) => (is_usa ? '0' : '24px')};
  margin-bottom: 32px;
`;

const EmailContainer = styled.div`
  margin-bottom: 52px;
`;

const EmailInputContainer = styled.div`
  margin-top: 22px;
`;

const AddressRow = styled.div`
  display: flex;
  jusifty-content: space-between;
`;

const StateField = styled(Select)`
  margin-right: 24px;
  height: auto;
  width: 49%;
`;

const ZipField = styled(TextField)`
  width: 49%;
`;

const PersonalEmailField = styled(TextField)`
  width: 100%;
`;

const NameNote = styled.div`
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
  background-color: ${({
    theme: {
      colors: {
        background: { secondary },
      },
    },
  }) => secondary};
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 8px;
  margin-bottom: 40px;
  border-radius: 7px;
  display: flex;
  align-items: center;
`;

const NameNoteText = styled.div`
  margin-left: 8px;
`;

const EmailNote = styled(Body2)`
  display: block;
  margin-top: 8px;
  color: ${({
    theme: {
      colors: {
        text: { secondary },
      },
    },
  }) => secondary};
`;

const SaveButton = styled(Button).attrs({
  variant: BUTTON_VARIANTS.PRIMARY,
  size: BUTTON_SIZES.LARGE,
})``;

const required = (value: $TSFixMe) =>
  value ? undefined : 'This field cannot be blank';
const emailRequired = (value: $TSFixMe) =>
  /\S+@\S+\.\S+/.test(value) ? undefined : 'Enter a valid email address';

const focusOnErrors = createDecorator();

const renderAddressForm = ({ is_usa, state_options }: $TSFixMe) =>
  is_usa ? (
    <ResidenceInputContainer>
      <AddressContainer>
        <Field
          id="street"
          name="street"
          validate={required}
          render={({ input, meta }) => (
            <StreetAddress1Field
              {...input}
              label="Street Address"
              autocomplete={AUTOCOMPLETE_TYPES.ADDRESS_LINE1}
              error={meta.touched && meta.error}
            />
          )}
        />

        <Field
          id="street_ext"
          name="street_ext"
          render={({ input, meta }) => (
            <StreetAddress2Field
              {...input}
              label="Apt, Building, Suite (optional)"
              autocomplete={AUTOCOMPLETE_TYPES.ADDRESS_LINE2}
              error={meta.touched && meta.error}
            />
          )}
        />

        <Field
          id="locality"
          name="locality"
          validate={required}
          render={({ input, meta }) => (
            <CityField
              is_usa={true}
              {...input}
              label="City"
              autocomplete={AUTOCOMPLETE_TYPES.ADDRESS_LEVEL2}
              error={meta.touched && meta.error}
            />
          )}
        />
      </AddressContainer>

      <AddressRow>
        <Field
          id="state"
          name="state"
          render={({ input, meta }) => (
            <StateField
              {...input}
              label="State"
              autocomplete={AUTOCOMPLETE_TYPES.ADDRESS_LEVEL1}
              error={meta.touched && meta.error}
            >
              {state_options.map(({ name, id }: $TSFixMe) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </StateField>
          )}
        />
        <Field
          id="zip"
          name="zip"
          validate={required}
          render={({ input, meta }) => (
            <ZipField
              {...input}
              label="Zip Code"
              autocomplete={AUTOCOMPLETE_TYPES.POSTAL_CODE}
              error={meta.touched && meta.error}
            />
          )}
        />
      </AddressRow>
    </ResidenceInputContainer>
  ) : (
    <ResidenceInputContainer>
      <AddressContainer>
        <Field
          id="street"
          name="street"
          validate={required}
          render={({ input, meta }) => (
            <StreetAddress1Field
              {...input}
              label="Address Line 1"
              autocomplete={AUTOCOMPLETE_TYPES.ADDRESS_LINE1}
              error={meta.touched && meta.error}
            />
          )}
        />

        <Field
          id="street_ext"
          name="street_ext"
          render={({ input, meta }) => (
            <StreetAddress2Field
              {...input}
              label="Address Line 2 (Optional)"
              autocomplete={AUTOCOMPLETE_TYPES.ADDRESS_LINE2}
              error={meta.touched && meta.error}
            />
          )}
        />
      </AddressContainer>

      <AddressRow>
        <Field
          id="locality"
          name="locality"
          validate={required}
          render={({ input, meta }) => (
            <CityField
              is_usa={false}
              {...input}
              label="Town/City"
              autocomplete={AUTOCOMPLETE_TYPES.ADDRESS_LEVEL2}
              error={meta.touched && meta.error}
            />
          )}
        />

        <Field
          id="zip"
          name="zip"
          validate={required}
          render={({ input, meta }) => (
            <ZipField
              {...input}
              label="Postcode"
              autocomplete={AUTOCOMPLETE_TYPES.POSTAL_CODE}
              error={meta.touched && meta.error}
            />
          )}
        />
      </AddressRow>
    </ResidenceInputContainer>
  );

const SettingsProfile = (props: $TSFixMe) => {
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
  const { employee, refetchProfile } = useContext(ProfileContext);
  const { pretax_accounts } = useContext(PretaxContext);
  const [uploading, setUploading] = useState(false);

  const onAvatarUpload = async (file: $TSFixMe) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await SettingApis.updateProfile(formData);
      await refetchProfile();
      showSuccessSnackbar({
        text: 'Your avatar has been updated.',
      });
      return true;
    } catch (e: $TSFixMe) {
      const {
        response: { data },
      } = e;
      const { message = 'An error occured.' } = data || {};
      showErrorSnackbar({
        text: message,
      });
    } finally {
      setUploading(false);
    }
  };

  const is_usa = EmployeeUtils.getCountry({ employee }) === 'us';
  const has_pretax_accounts = EmployeeUtils.hasPretaxAccounts({
    employee,
    pretax_accounts,
  });

  const state_options = Object.keys(US_STATES).map((state) => ({
    id: state,
    name: state,
  }));

  const submitForm = async ({
    preferred_first_name,
    street,
    street_ext,
    locality,
    state,
    zip,
    personal_email,
  }: $TSFixMe) => {
    const address = {
      street,
      street_ext,
      locality,
      state,
      zip,
    };

    const formData = {
      personal_email: personal_email || '',
      preferred_first_name: preferred_first_name || '',
      shipping_address: address,
    };
    try {
      await SettingApis.updateProfile(formData);
      showSuccessSnackbar({
        text: 'Your profile has been updated.',
      });
    } catch (e: $TSFixMe) {
      const {
        response: { data },
      } = e;
      const { message = 'An error occured.' } = data || {};
      showErrorSnackbar({
        text: message,
      });
    }
  };
  return (
    <Container>
      <Head title={'Settings - Profile'} />
      <Skeleton loading={!employee} active>
        {employee && (
          <Form
            onSubmit={({
              preferred_first_name,
              street,
              street_ext,
              locality,
              state,
              zip,
              personal_email,
            }) =>
              submitForm({
                preferred_first_name,
                street,
                street_ext,
                locality,
                state,
                zip,
                personal_email,
              })
            }
            decorators={[focusOnErrors]}
            initialValues={{ ...employee, ...employee?.settings?.address }}
            render={({ handleSubmit, submitting }) => (
              <FormContainer onSubmit={handleSubmit}>
                <FieldsContainer
                  data-testid={EMPLOYEE_PROFILE_ELEMENTS.EMPLOYEE_INFO}
                >
                  <PageHeader>Profile</PageHeader>
                  <Avatar
                    uploading={uploading}
                    avatar_url={employee.avatar_url}
                    onAvatarUpload={onAvatarUpload}
                  />

                  <SectionHeader>Your Information</SectionHeader>
                  <InfoInputContainer>
                    <NameRow>
                      <Field
                        name="first_name"
                        render={({ meta }) => (
                          <FirstNameField
                            value={employee.first_name}
                            label="First Name"
                            autocomplete={AUTOCOMPLETE_TYPES.GIVEN_NAME}
                            error={meta.touched && meta.error}
                          />
                        )}
                      />
                      <Field
                        name="last_Name"
                        render={({ meta }) => (
                          <LastNameField
                            value={employee.last_name}
                            label="Last Name"
                            autocomplete={AUTOCOMPLETE_TYPES.FAMILY_NAME}
                            error={meta.touched && meta.error}
                          />
                        )}
                      />
                    </NameRow>
                    <PreferredNameRow>
                      <Field
                        id="preferred_first_name"
                        name="preferred_first_name"
                        render={({ input, meta }) => (
                          <PreferredNameField
                            {...input}
                            label="Preferred Name (optional)"
                            autocomplete={AUTOCOMPLETE_TYPES.NICKNAME}
                            error={meta.touched && meta.error}
                          />
                        )}
                      />
                    </PreferredNameRow>
                  </InfoInputContainer>

                  <NameNote>
                    <StyledHelpIcon />
                    <NameNoteText>
                      Need to change your name? Please contact your HR or admin
                      to make any changes.
                    </NameNoteText>
                  </NameNote>

                  <SectionHeader>Primary Residence</SectionHeader>
                  {renderAddressForm({ is_usa, state_options })}
                  {is_usa && has_pretax_accounts && (
                    <EmailContainer>
                      <SectionHeader>Personal Email</SectionHeader>
                      <EmailInputContainer>
                        <Field
                          id="personal_email"
                          name="personal_email"
                          validate={emailRequired}
                          type="email"
                          render={({ input, meta }) => (
                            <PersonalEmailField
                              {...input}
                              label="Email Address"
                              autocomplete={AUTOCOMPLETE_TYPES.EMAIL}
                              error={meta.touched && meta.error}
                            />
                          )}
                        />
                        <EmailNote>
                          We will only email you receipt and order updates.
                        </EmailNote>
                      </EmailInputContainer>
                    </EmailContainer>
                  )}
                </FieldsContainer>
                <SaveButton loading={submitting} onClick={handleSubmit}>
                  Save Changes
                </SaveButton>
              </FormContainer>
            )}
          />
        )}
      </Skeleton>
    </Container>
  );
};

export default SettingsProfile;
